import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useReceipts } from 'hooks/api/useBanking';
import { coaLedgerService } from 'api/services';
import type { ReceiptDto, ReceiptQueryParams } from 'api/types';
import {
  ReceiptFormModal, ReceiptViewModal, ReceiptAssignModal, receiptStatusBadge, assignmentStatusBadge,
} from '../../modals/modules/banking/ReceiptFormModal';

// ============================================================================
// RECEIPT SCREEN (ADR-0024 Slice ① Leg 1) — entity-level receipt register.
// Capture posts the Bank Receipt voucher on save; the Assign row action
// distributes the unassigned remainder (or redistributes a RETURNED portion).
// ============================================================================

type ViewMode = 'list' | 'grid';

interface ReceiptScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');
const fmtMoney = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const EPS = 0.005;

const DOC_STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'CAPTURED', label: 'Captured' },
  { value: 'PARTIALLY_ALLOCATED', label: 'Partially Allocated' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const ReceiptScreen: React.FC<ReceiptScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('receipt');
  const { ConfirmDialogPortal } = useStandardConfirms();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [queryParams, setQueryParams] = useState<ReceiptQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useReceipts(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const bankLabelCache = useRef<Map<string, string>>(new Map());

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ReceiptDto | null>(null);

  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  const handleAddNew = useCallback(() => {
    if (formModalOpen) formModalMinimizeRef.current?.();
    let next = 1;
    if (tabId) {
      while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    }
    setCreateInstanceId(String(next));
    setFormModalOpen(true);
  }, [formModalOpen, tabId, isMinimized]);

  const handleView = useCallback((id: string) => {
    const record = data.find(r => r.id === id);
    if (!record) return;
    const key = record.receiptNo || record.id;
    const viewId = tabId ? `${tabId}-view-modal-${key}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setSelectedRecord(record); setViewModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleAssign = useCallback((id: string) => {
    const record = data.find(r => r.id === id);
    if (!record) return;
    setSelectedRecord(record); setAssignModalOpen(true);
  }, [data]);

  const handleRestoreModal = useCallback((_mode: 'create', instanceId?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    setFormModalOpen(true);
  }, []);

  const handleRestoreView = useCallback((key: string) => {
    const rec = data.find(r => (r.receiptNo || r.id) === key);
    if (rec) { setSelectedRecord(rec); setViewModalOpen(true); }
  }, [data]);

  const handleSaved = useCallback(() => {
    setFormModalOpen(false); refetch();
  }, [refetch]);

  // Assign is possible while the receipt still has an unassigned remainder or a
  // returned portion to redistribute. The list omits assignments, so RETURNED
  // portions are detected coarsely: any non-CLOSED, non-CANCELLED receipt may
  // hold one — the Assign modal fetches the detail and shows what's assignable.
  const canAssign = useCallback((r: ReceiptDto): boolean => {
    if (!can('UPDATE')) return false;
    if (r.docStatus === 'CANCELLED' || r.docStatus === 'CLOSED') return false;
    // unassigned > 0, or a RETURNED portion may exist (not visible in the list).
    return true;
  }, [can]);

  const tableColumns: SyutGridColumn<ReceiptDto>[] = useMemo(() => [
    {
      id: 'receiptNo', header: 'Receipt No', accessorKey: 'receiptNo', size: 140,
      cell: ({ row }) => (row.original as ReceiptDto).receiptNo || '—',
    },
    {
      id: 'receiptDate', header: 'Date', accessorKey: 'receiptDate', size: 105,
      cell: ({ row }) => fmtDate((row.original as ReceiptDto).receiptDate),
    },
    {
      id: 'bank', header: 'Bank', accessorKey: 'bankLedgerName', size: 160,
      cell: ({ row }) => (row.original as ReceiptDto).bankLedgerName || '—',
    },
    {
      id: 'payer', header: 'Payer / Customer', size: 180,
      cell: ({ row }) => {
        const r = row.original as ReceiptDto;
        return r.partyName || r.payerText || '—';
      },
    },
    {
      id: 'amount', header: 'Amount', accessorKey: 'fcAmount', size: 130,
      cell: ({ row }) => {
        const r = row.original as ReceiptDto;
        return <span style={{ display: 'block', textAlign: 'right' }}>{fmtMoney(r.fcAmount)} {r.currencyCode || ''}</span>;
      },
    },
    {
      id: 'unassigned', header: 'Unassigned', accessorKey: 'unassignedFcAmount', size: 115,
      cell: ({ row }) => {
        const r = row.original as ReceiptDto;
        const v = r.unassignedFcAmount;
        return (
          <span style={{ display: 'block', textAlign: 'right', fontWeight: v > EPS ? 700 : 400, color: v > EPS ? '#d97706' : 'var(--text-muted)' }}>
            {fmtMoney(v)}
          </span>
        );
      },
    },
    {
      id: 'assignedBus', header: 'Assigned BUs', size: 190,
      cell: ({ row }) => {
        const r = row.original as ReceiptDto;
        const assignments = r.assignments || [];
        if (assignments.length === 0) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
        return (
          <span style={{ display: 'inline-flex', gap: '0.25rem', flexWrap: 'wrap' }}>
            {assignments.map(a => {
              // Cross-entity portion (ADR-0026) — entityId differs from the receipt's.
              const viaIc = (!!a.entityId && a.entityId !== r.entityId) || !!a.icOutTransactionId;
              return (
                <span key={a.id} className="badge"
                  title={`${a.businessUnitName || 'BU'} · ${fmtMoney(a.fcAmount)} · ${a.assignmentStatus}${viaIc ? ' · via intercompany transfer' : ''}`}
                  style={a.assignmentStatus === 'RETURNED' ? { background: '#dc2626', color: '#fff' } : undefined}>
                  {viaIc ? '⇄ ' : ''}{a.businessUnitName || 'BU'}
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'docStatus', size: 145,
      cell: ({ row }) => receiptStatusBadge((row.original as ReceiptDto).docStatus),
    },
    {
      id: 'createdBy', header: 'Created By', accessorKey: 'createdByName', size: 130,
      cell: ({ row }) => (row.original as ReceiptDto).createdByName || '—',
    },
  ], []);

  const getRowActions = useCallback((rec: ReceiptDto): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(rec.id) },
    ];
    if (canAssign(rec)) {
      actions.push({ id: 'assign', label: 'Assign', icon: <IconSystem.link size={18} />, onClick: () => handleAssign(rec.id) });
    }
    return actions;
  }, [canAssign, handleView, handleAssign]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAddNew, 'New Receipt'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const loadBankOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined, ledgerNature: 'BANK' });
      res.data.forEach(l => bankLabelCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return [
        { value: '', label: 'All Banks' },
        ...res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` })),
      ];
    } catch {
      return [{ value: '', label: 'All Banks' }];
    }
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      docStatus: statusFilter || undefined,
      bankLedgerId: bankFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      unassignedOnly: unassignedOnly || undefined,
      includeCancelled: includeCancelled || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [statusFilter, bankFilter, fromDate, toDate, unassignedOnly, includeCancelled]);

  const clearFilters = useCallback(() => {
    setStatusFilter(''); setBankFilter(''); setFromDate(''); setToDate('');
    setUnassignedOnly(false); setIncludeCancelled(false);
    setQueryParams(prev => ({ page: 1, pageSize: prev.pageSize }));
  }, []);

  const appliedCount = [statusFilter, bankFilter, fromDate, toDate, unassignedOnly ? 'x' : '', includeCancelled ? 'x' : ''].filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Receipts"
      description="Capture money-in at entity level and assign it to business units — post-on-create (ADR-0024)"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.dollarSign size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search receipt no / payer / reference / customer...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Status" options={DOC_STATUS_OPTIONS} value={statusFilter}
                  onChange={(v) => setStatusFilter((v as string) || '')}
                  placeholder="All Statuses" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Bank" isAsync loadOptions={loadBankOptions}
                  defaultOptions cacheOptions={false} value={bankFilter}
                  seedOptions={bankFilter ? [{ value: bankFilter, label: bankLabelCache.current.get(bankFilter) || '' }] : undefined}
                  onChange={(v) => setBankFilter((v as string) || '')}
                  placeholder="All Banks" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={unassignedOnly}
                    onChange={e => setUnassignedOnly(e.target.checked)} /> Unassigned only
                </label>
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeCancelled}
                    onChange={e => setIncludeCancelled(e.target.checked)} /> Include cancelled
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={tableColumns} enableRowSelection
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage={loading ? 'Loading...' : 'No receipts yet — click Add to capture one.'}
            frozenRightColumns={['actions']} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.dollarSign size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading...' : 'No receipts yet.'}</p>
                </div>
              ) : (
                data.map((r) => (
                  <div key={r.id} style={{
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{r.receiptNo || '—'}</div>
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0.25rem 0 0' }}>
                          {fmtMoney(r.fcAmount)} {r.currencyCode || ''}
                        </h3>
                      </div>
                      <ActionMenu items={getRowActions(r)} />
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {r.bankLedgerName || '—'} · {fmtDate(r.receiptDate)}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {r.partyName || r.payerText || r.reference || '—'}
                    </div>
                    <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {receiptStatusBadge(r.docStatus)}
                      {r.unassignedFcAmount > EPS && (
                        <span className="badge" style={{ background: '#d97706', color: '#fff' }}>
                          Unassigned {fmtMoney(r.unassignedFcAmount)}
                        </span>
                      )}
                      {(r.assignments || []).filter(a => a.assignmentStatus === 'RETURNED').map(a => (
                        <span key={a.id}>{assignmentStatusBadge(a.assignmentStatus)}</span>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <select value={queryParams.pageSize ?? 25}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
                    {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} / page</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ReceiptFormModal isOpen={formModalOpen}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId} moduleName={moduleName}
        createInstanceId={createInstanceId}
        onRestoreModal={handleRestoreModal}
        minimizeRef={formModalMinimizeRef} />

      <ReceiptViewModal isOpen={viewModalOpen} receipt={selectedRecord}
        onClose={() => { setViewModalOpen(false); setSelectedRecord(null); }}
        tabId={tabId} moduleName={moduleName}
        onRestoreView={handleRestoreView} />

      <ReceiptAssignModal isOpen={assignModalOpen} receipt={selectedRecord}
        onAssigned={refetch}
        onClose={() => { setAssignModalOpen(false); setSelectedRecord(null); }} />
    </EnterpriseScreenShell>
  );
};

export default ReceiptScreen;
